import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Stack, Autocomplete,
    TablePagination, InputAdornment
} from '@mui/material';
import { Add, Delete, Print, Search } from '@mui/icons-material';
import { collection, addDoc, getDocs, getDoc, query, orderBy, deleteDoc, doc, where, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from 'sweetalert2';

const PRIMARY = "#0d9488";

function Studentinpass() {
    const [inPasses, setInPasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [schoolId, setSchoolId] = useState(null);
    const [printData, setPrintData] = useState(null);

    // Filter, Search and Pagination States
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        studentId: '',
        studentName: '',
        gradeClass: '',
        parentsName: '',
        parentsContact: '',
        parentsAdhar: '',
        address: '',
        vehicleNo: '',
        emergencyNo: '',
        relation: '',
        date: new Date().toISOString().split('T')[0],
        day: new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date()),
        outTime: '',
    });

    useEffect(() => {
        const init = async () => {
            if (!auth.currentUser) return;
            try {
                const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
                if (userSnap.exists()) {
                    const sId = userSnap.data().schoolId;
                    setSchoolId(sId);
                    fetchBoardingStudents(sId);
                    fetchInPasses(sId);
                }
            } catch (err) {
                console.error("Initialization error:", err);
            }
        };
        init();
    }, []);

    const getDayFromDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
    };

    const fetchBoardingStudents = async (sId) => {
        try {
            const [admSnap, studentSnap, courseSnap] = await Promise.all([
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("studenttype", "==", "Boarding"), where('status', '==', 'Active'))),
                getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "courses"), where("schoolId", "==", sId)))
            ]);

            const studentMap = {};
            studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });
            const courseMap = {};
            courseSnap.docs.forEach(d => { courseMap[d.id] = d.data(); });

            const list = admSnap.docs.map((adm) => {
                const admData = adm.data();
                const sData = studentMap[admData.studentId];
                return {
                    id: admData.studentId,
                    name: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''}`.toUpperCase(),
                    className: courseMap[admData.class]?.name || "N/A",
                    parentName: sData?.fathername || sData?.fatherName || '',
                    parentContact: sData?.fathermobile || '',
                    adhar: sData?.father_adharno || sData?.adharno || '',
                    address: sData?.address || '',
                    label: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''} - ${courseMap[admData.class]?.name || ''}`.toUpperCase()
                };
            });
            setStudents(list);
        } catch (err) { 
            console.error("Error fetching students:", err); 
        }
    };

    const fetchInPasses = async (sId) => {
        try {
            const q = query(collection(db, "hostel_in_passes"), where("schoolId", "==", sId), orderBy("timestamp", "desc"));
            const snap = await getDocs(q);
            setInPasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching passes:", err);
        }
    };

    const handleStudentSelect = (val) => {
        if (val) {
            setFormData(prev => ({
                ...prev,
                studentId: val.id,
                studentName: val.name,
                gradeClass: val.className,
                parentsName: val.parentName,
                parentsContact: val.parentContact,
                parentsAdhar: val.adhar,
                address: val.address
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                studentId: '',
                studentName: '',
                gradeClass: '',
                parentsName: '',
                parentsContact: '',
                parentsAdhar: '',
                address: ''
            }));
        }
    };

    const handleSave = async () => {
        if (!formData.studentId) return Swal.fire("Error", "Please select a student", "error");
        setLoading(true);
        try {
            const data = { ...formData, schoolId, timestamp: Timestamp.now() };
            const passNo = inPasses.length > 0 ? (Number(inPasses[0].passNo) + 1) : 101;
            await addDoc(collection(db, "hostel_in_passes"), { ...data, passNo });

            setOpen(false);
            fetchInPasses(schoolId);
            Swal.fire("Success", "In-Pass Saved", "success");
            
            // Reset state parameters
            setFormData({
                studentId: '',
                studentName: '',
                gradeClass: '',
                parentsName: '',
                parentsContact: '',
                parentsAdhar: '',
                address: '',
                vehicleNo: '',
                emergencyNo: '',
                relation: '',
                date: new Date().toISOString().split('T')[0],
                day: new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date()),
                outTime: '',
            });
        } catch (e) { 
            Swal.fire("Error", e.message, "error"); 
        }
        setLoading(false);
    };

    const triggerPrint = (item) => {
        setPrintData(item);
        setTimeout(() => {
            window.print();
        }, 500);
    };

    // Filter Logic setup
    const filteredInPasses = inPasses.filter(item => 
        (item.studentName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
        (item.gradeClass?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
        (item.passNo?.toString() || "").includes(searchQuery)
    );

    // Paginated View List Slicing
    const paginatedInPasses = filteredInPasses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box p={2} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <style>
                {`
                @media screen {
                    #print-area { display: none; }
                }
                @media print {
                    body * { visibility: hidden; }
                    #print-area, #print-area * { visibility: visible; }
                    #print-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        display: block !important;
                    }
                }
                .pass-container { 
                    border: 2px solid #000; 
                    padding: 20px; 
                    width: 210mm; 
                    font-family: 'Arial', sans-serif; 
                    background: #fff;
                }
                .dotted-line { 
                    border-bottom: 1px dotted #000; 
                    display: inline-block; 
                    min-width: 140px; 
                    padding: 0 5px; 
                    font-weight: bold; 
                }
                `}
            </style>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} mb={3}>
                <Typography variant="h6" fontWeight="bold">Hostel Student In-Pass Records</Typography>
                {/* <Button variant="contained" sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0f766e' } }} startIcon={<Add />} onClick={() => setOpen(true)}>
                    Generate In-Pass
                </Button> */}
            </Stack>

            {/* SEARCH BOX CONTROL */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0' }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search by Pass No, Student Name, or Class..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" size="small"/>
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {/* TABLE AND PAGINATION */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Pass No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Class</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Parent / Guardian</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contact No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Arrival Info</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedInPasses.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    No records found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedInPasses.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell><b>#{item.passNo}</b></TableCell>
                                    <TableCell><b>{item.studentName}</b></TableCell>
                                    <TableCell>{item.gradeClass}</TableCell>
                                    <TableCell>{item.parentsName} {item.relation ? `(${item.relation})` : ''}</TableCell>
                                    <TableCell>{item.parentsContact}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.date}</Typography>
                                        <Typography variant="caption" color="textSecondary">{item.outTime || 'N/A'} ({item.day})</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => triggerPrint(item)} size="small">
                                            <Print fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredInPasses.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </TableContainer>

            {/* GENERATION DIALOG */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Hostel Student In-Pass Form</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ mt: 0.5 }}>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <Autocomplete
                                options={students}
                                getOptionLabel={(o) => o.label || ""}
                                onChange={(e, v) => handleStudentSelect(v)}
                                renderInput={(params) => <TextField {...params} label="Search Student" size="small" fullWidth required />}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField
                                fullWidth label="Date" type="date" size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.date}
                                onChange={e => setFormData({ ...formData, date: e.target.value, day: getDayFromDate(e.target.value) })}
                            />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="Day (Auto)" size="small" value={formData.day} InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="In Time" type="time" size="small" InputLabelProps={{ shrink: true }} value={formData.outTime} onChange={e => setFormData({ ...formData, outTime: e.target.value })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="Parents Name" size="small" value={formData.parentsName} onChange={e => setFormData({ ...formData, parentsName: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="Relation with Student" size="small" value={formData.relation} onChange={e => setFormData({ ...formData, relation: e.target.value })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="Parents Vehicle No" size="small" value={formData.vehicleNo} onChange={e => setFormData({ ...formData, vehicleNo: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="Parents Aadhaar No" size="small" value={formData.parentsAdhar} onChange={e => setFormData({ ...formData, parentsAdhar: e.target.value })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="Emergency Contact No" size="small" value={formData.emergencyNo} onChange={e => setFormData({ ...formData, emergencyNo: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12}>
                            <TextField fullWidth label="Address" multiline rows={2} size="small" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0f766e' } }} onClick={handleSave} disabled={loading}>
                        Save In-Pass
                    </Button>
                </DialogActions>
            </Dialog>

            {/* INTERACTIVE PRINT PRINT-AREA BOX */}
            <div id="print-area">
                {printData && (
                    <div className="pass-container" style={{
                        width: '310mm', 
                        minHeight: '130mm',
                        padding: '0', 
                        backgroundColor: 'white',
                        margin: '20px auto',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        display: 'flex',
                        overflow: 'hidden',
                        fontFamily: "'Inter', sans-serif",
                        position: 'relative'
                    }}>
                        <Box sx={{
                            width: '60px',
                            bgcolor: '#0f172a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                            color: 'white'
                        }}>
                            <Typography variant="h6" sx={{ letterSpacing: '4px', fontWeight: 'bold', opacity: 0.8 }}>
                                HOSTEL IN-PASS
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, p: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, borderBottom: '2px solid #f1f5f9', pb: 2 }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: '600', letterSpacing: '1px' }}>
                                        ABHINAV FOUNDATION'S
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>
                                        MOHAN MALI <span style={{ color: '#0d9488' }}>INTERNATIONAL</span> SCHOOL
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                        Kavathe Mahankal, Sangli | Maharashtra
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="h6" sx={{ fontWeight: '900', color: '#0d9488' }}>
                                        #{printData.passNo}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: '4px' }}>
                                        ENTRY PASS
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid className="col-sm-4 mb-3" item xs={12}>
                                    <Stack direction="row" spacing={4} sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">ARRIVAL DATE</Typography>
                                            <Typography variant="body1" fontWeight="bold">{printData.date} ({printData.day})</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">IN TIME</Typography>
                                            <Typography variant="body1" fontWeight="bold">{printData.outTime || 'N/A'}</Typography>
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid className="col-sm-4 mb-3" item xs={7}>
                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>STUDENT INFORMATION</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: '700', color: '#1e293b' }}>{printData.studentName}</Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>Class: <b>{printData.gradeClass}</b></Typography>

                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>PARENT/GUARDIAN DETAILS</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: '600' }}>{printData.parentsName} {printData.relation ? <span style={{ fontSize: '12px', color: '#94a3b8' }}>({printData.relation})</span> : ''}</Typography>
                                    <Typography variant="body2" color="textSecondary">Contact: {printData.parentsContact}</Typography>
                                    <Typography variant="body2" color="textSecondary">Aadhaar: [Redacted]</Typography>
                                </Grid>

                                <Grid className="col-sm-4 mb-3" item xs={5} sx={{ borderLeft: '1px dashed #cbd5e1', pl: 3 }}>
                                    <Box mb={2}>
                                        <Typography variant="caption" color="textSecondary">VEHICLE NO.</Typography>
                                        <Typography variant="body2" fontWeight="bold">{printData.vehicleNo || 'N/A'}</Typography>
                                    </Box>
                                    <Box mb={2}>
                                        <Typography variant="caption" color="textSecondary">EMERGENCY CONTACT</Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#e11d48' }}>{printData.emergencyNo || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">ADDRESS</Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{printData.address || 'N/A'}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #f1f5f9' }}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Box sx={{ textAlign: 'center', width: '150px' }}>
                                        <Box sx={{ height: '40px' }} /> 
                                        <Typography variant="caption" sx={{ display: 'block', borderTop: '1px solid #cbd5e1', pt: 1, fontWeight: 'bold' }}>
                                            PARENT SIGN
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', width: '150px' }}>
                                        <Box sx={{ height: '40px' }} />
                                        <Typography variant="caption" sx={{ display: 'block', borderTop: '1px solid #cbd5e1', pt: 1, fontWeight: 'bold' }}>
                                            STUDENT SIGN
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', width: '180px' }}>
                                        <Box sx={{ height: '40px' }} />
                                        <Typography variant="caption" sx={{ display: 'block', borderTop: '1px solid #cbd5e1', pt: 1, fontWeight: 'bold' }}>
                                            AUTHORIZED SIGNATORY
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Stack direction="row" spacing={2} justifyContent="center" mt={4} sx={{ opacity: 0.6 }}>
                                <Typography variant="caption" sx={{ border: '1px solid #ddd', px: 1, borderRadius: '4px' }}>WARDEN</Typography>
                                <Typography variant="caption" sx={{ border: '1px solid #ddd', px: 1, borderRadius: '4px' }}>ACCOUNTS</Typography>
                                <Typography variant="caption" sx={{ border: '1px solid #ddd', px: 1, borderRadius: '4px' }}>OFFICE</Typography>
                            </Stack>
                        </Box>
                    </div>
                )}
            </div>
        </Box>
    );
}

export default Studentinpass;